import React, { useState, useRef, useEffect, useCallback } from 'react';
import { SmartSegmentationHeader } from '../components/SmartSegmentationHeader';
import { Footer } from '../components/Footer';
import { ChevronDownIcon, CheckCircleIcon, XIcon, LoaderIcon } from 'lucide-react';
import { SegmentDashboardMockup } from '../components/SegmentDashboardMockup';
import { SponsorReportMockup } from '../components/SponsorReportMockup';
import { FlowDiagramMockup } from '../components/FlowDiagramMockup';
import { OverwhelmedCreatorMockup } from '../components/OverwhelmedCreatorMockup';
import { getBaseUrl } from '../utils/api';
export function SmartSegmentationPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [cognitoId, setCognitoId] = useState<string | null>(null);
  const [userActivities, setUserActivities] = useState<string[]>([]);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [hasSignedUpViaEmail, setHasSignedUpViaEmail] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailDialogRef = useRef<HTMLDivElement>(null);

  // Check localStorage for email signup on mount
  useEffect(() => {
    const emailSignupStatus = localStorage.getItem('ss_beta_join_email');
    if (emailSignupStatus === 'true') {
      setHasSignedUpViaEmail(true);
    }
  }, []);

  // Function to fetch user data
  const fetchUserData = useCallback(async (id: string) => {
    setIsLoadingUser(true);
    try {
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}user?cognitoId=${id}`);
      if (response.ok) {
        const data = await response.json();
        if (data && data.user) {
          setUserName(data.user.name);
          setUserActivities(data.user.activity || []);
        }
      }
    } catch (err) {
      console.error('Failed to fetch user:', err);
    } finally {
      setIsLoadingUser(false);
    }
  }, []);

  // Fetch user data on page load if id parameter exists
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (id) {
      setCognitoId(id);
      fetchUserData(id);
    }
  }, [fetchUserData]);

  // Handle email dialog outside clicks and ESC key
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emailDialogRef.current && !emailDialogRef.current.contains(event.target as Node)) {
        setShowEmailDialog(false);
      }
    };
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowEmailDialog(false);
      }
    };
    if (showEmailDialog) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscKey);
        document.body.style.overflow = 'auto';
      };
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'auto';
    };
  }, [showEmailDialog]);

  // Reset email input when dialog closes
  useEffect(() => {
    if (!showEmailDialog) {
      setEmailInput('');
    }
  }, [showEmailDialog]);
  const handleJoinBetaClick = async (e: React.MouseEvent) => {
    e.preventDefault();

    // If user is known (has cognitoId and userName), POST activity directly
    if (cognitoId && userName) {
      setIsSubmitting(true);
      try {
        const baseUrl = getBaseUrl();
        const response = await fetch(`${baseUrl}user/activity`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            activity_name: 'ss_beta_join',
            cognito_id: cognitoId
          })
        });
        if (response.ok) {
          console.log('Activity logged successfully');
          // Re-fetch user data to update activities
          if (cognitoId) {
            await fetchUserData(cognitoId);
          }
        } else {
          console.error('Failed to log activity');
        }
      } catch (error) {
        console.error('Error logging activity:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // User not known - show email dialog
      setShowEmailDialog(true);
    }
  };
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput) return;
    setIsSubmitting(true);
    try {
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}user/activity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          activity_name: 'ss_beta_join',
          cognito_id: '1',
          activity_data: {
            email: emailInput
          }
        })
      });
      if (response.ok) {
        // Save signup status to localStorage
        localStorage.setItem('ss_beta_join_email', 'true');
        setHasSignedUpViaEmail(true);
        setShowEmailDialog(false);
        setEmailInput('');
        console.log('Email submitted successfully');
      } else {
        console.error('Failed to submit email');
      }
    } catch (error) {
      console.error('Error submitting email:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  // Extract first name from full name
  const firstName = userName ? userName.split(' ')[0] : null;

  // Check if user has already signed up for beta (via API or email)
  const isSignedUpForBeta = userActivities.includes('ss_beta_join') || hasSignedUpViaEmail;
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    question: 'How does this connect to Kit.com?',
    answer: 'You securely connect your Kit.com account, and we read your opens, clicks, and product data to build segments. No extra tools required.'
  }, {
    question: 'Is my data safe?',
    answer: "Yes. Your data is encrypted, only used to power your own segments and insights, and you can disconnect at any time. We don't sell your data."
  }, {
    question: 'Who is this for?',
    answer: 'Newsletter creators who send via Kit.com, have a growing list, and either run sponsorships now or plan to soon.'
  }, {
    question: 'What does "beta" mean?',
    answer: 'You get early access, closer support, and a say in the roadmap. The product will improve quickly based on feedback.'
  }, {
    question: 'How much does it cost?',
    answer: 'Pricing is still being finalized. Beta users get early adopter pricing and will see plans before committing to anything.'
  }, {
    question: 'How much setup is required?',
    answer: "Connect Kit.com, let us process your data, review your segments, and you're ready to use them in campaigns and pitches."
  }];
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-1510">
    <SmartSegmentationHeader data-id="element-1511" />

    {isLoadingUser ? <main className="flex-grow pt-24 flex items-center justify-center min-h-[60vh]" data-id="element-1512">
      <div className="text-center" data-id="element-1513">
        <LoaderIcon className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" data-id="element-1514" />
        <p className="text-gray-600 text-lg" data-id="element-1515">Loading...</p>
      </div>
    </main> : <main className="flex-grow pt-24" data-id="element-1516">
      {/* Hero Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-id="element-1517">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1518">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-full mb-6" data-id="element-1519">
            <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" data-id="element-1520"></span>
            <span className="text-sm font-medium text-blue-700" data-id="element-1521">
              Always-on segmentation for Kit.com creators
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-1522">
            {firstName ? `Still manually tagging subscribers, ${firstName}? This was built for you` : 'Still manually tagging subscribers? This was built for you'}
          </h1>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto" data-id="element-1523">
            AI that turns your data into automated smart segments, so your
            audience stays organized, personalized, and sponsor-ready without
            you touching a single tag.
          </p>

          <ul className="space-y-3 mb-8 inline-block text-left" data-id="element-1524">
            <li className="flex items-start text-lg" data-id="element-1525">
              <span className="mr-3" data-id="element-1526">🎯</span>
              <span data-id="element-1527">Send targeted content to the right people</span>
            </li>
            <li className="flex items-start text-lg" data-id="element-1528">
              <span className="mr-3" data-id="element-1529">⏱</span>
              <span data-id="element-1530">Save hours by killing manual tagging</span>
            </li>
            <li className="flex items-start text-lg" data-id="element-1531">
              <span className="mr-3" data-id="element-1532">💸</span>
              <span data-id="element-1533">Pitch higher-value, segment-based sponsorships</span>
            </li>
          </ul>

          <div data-id="element-1534">
            {isSignedUpForBeta ? <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 max-w-md mx-auto" data-id="element-1535">
              <p className="text-xl font-semibold text-gray-900 text-center mb-2" data-id="element-1536">
                🎉 You're signed up for the beta! 🎉
              </p>
              <p className="text-gray-700 text-center" data-id="element-1537">
                You will hear back from us soon.
              </p>
            </div> : <>
              {firstName && <p className="text-lg text-gray-700 mb-4" data-id="element-1538">
                {firstName}, ready to join the beta?
              </p>}
              <button onClick={handleJoinBetaClick} disabled={isSubmitting} className="inline-block px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1539">
                Join the Beta
              </button>
              <p className="text-sm text-gray-500 mt-3" data-id="element-1540">
                Early access • Limited spots for Kit.com creators
              </p>
            </>}
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gray-50" data-id="element-1541">
        <div className="max-w-6xl mx-auto" data-id="element-1542">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1543">
            <div className="order-2 lg:order-1" data-id="element-1544">
              <OverwhelmedCreatorMockup data-id="element-1545" />
            </div>

            <div className="order-1 lg:order-2" data-id="element-1546">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-1547">
                The problem: you're doing ops instead of growing your
                newsletter
              </h2>
              <p className="text-lg text-gray-700 mb-6" data-id="element-1548">
                Right now, you probably:
              </p>
              <ul className="space-y-3 mb-6" data-id="element-1549">
                <li className="flex items-start text-lg text-gray-700" data-id="element-1550">
                  <span className="mr-3 text-gray-400" data-id="element-1551">•</span>
                  <span data-id="element-1552">
                    Manually tag subscribers or avoid tagging altogether
                  </span>
                </li>
                <li className="flex items-start text-lg text-gray-700" data-id="element-1553">
                  <span className="mr-3 text-gray-400" data-id="element-1554">•</span>
                  <span data-id="element-1555">
                    Send the same email to totally different types of readers
                  </span>
                </li>
                <li className="flex items-start text-lg text-gray-700" data-id="element-1556">
                  <span className="mr-3 text-gray-400" data-id="element-1557">•</span>
                  <span data-id="element-1558">
                    Share generic, one-size-fits-all stats with sponsors
                  </span>
                </li>
              </ul>
              <p className="text-xl font-semibold text-gray-900" data-id="element-1559">
                Result:{' '}
                <span className="text-blue-600" data-id="element-1560">
                  time wasted, generic content, and money left on the table.
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-id="element-1561">
        <div className="max-w-6xl mx-auto" data-id="element-1562">
          <div className="text-center mb-16" data-id="element-1563">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1564">
              Let your Kit.com list segment itself
            </h2>
          </div>

          {/* How it works */}
          <div className="mb-16" data-id="element-1565">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center" data-id="element-1566">
              How it works
            </h3>

            <div className="flex items-center justify-center mb-12" data-id="element-1567">
              <div className="w-full max-w-5xl" data-id="element-1568">
                <FlowDiagramMockup data-id="element-1569" />
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1570">
            <div className="flex items-start" data-id="element-1571">
              <CheckCircleIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1572" />
              <div data-id="element-1573">
                <h4 className="font-semibold text-gray-900 mb-1" data-id="element-1574">
                  Hands-free segmentation
                </h4>
                <p className="text-gray-600" data-id="element-1575">
                  No rules to maintain, no manual tagging.
                </p>
              </div>
            </div>
            <div className="flex items-start" data-id="element-1576">
              <CheckCircleIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1577" />
              <div data-id="element-1578">
                <h4 className="font-semibold text-gray-900 mb-1" data-id="element-1579">
                  Clear "who's who"
                </h4>
                <p className="text-gray-600" data-id="element-1580">
                  Instantly see segments like "Deal hunters," "Founders," or
                  "Sponsor clickers."
                </p>
              </div>
            </div>
            <div className="flex items-start" data-id="element-1581">
              <CheckCircleIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1582" />
              <div data-id="element-1583">
                <h4 className="font-semibold text-gray-900 mb-1" data-id="element-1584">
                  Make your newsletter feel 1:1
                </h4>
                <p className="text-gray-600" data-id="element-1585">
                  Effortlessly send different messages and offers to each
                  segment.
                </p>
              </div>
            </div>
            <div className="flex items-start" data-id="element-1586">
              <CheckCircleIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-1" data-id="element-1587" />
              <div data-id="element-1588">
                <h4 className="font-semibold text-gray-900 mb-1" data-id="element-1589">
                  Sponsor-ready segment stats
                </h4>
                <p className="text-gray-600" data-id="element-1590">
                  Use segment data directly in media kits and pitches.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gray-50" data-id="element-1591">
        <div className="max-w-6xl mx-auto" data-id="element-1592">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center" data-id="element-1593">
            What you get with Smart Audience Segmentation
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-1594">
            <div className="bg-white rounded-xl p-8" data-id="element-1595">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1596">
                Save time & mental energy
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-1597">
                <li className="flex items-start" data-id="element-1598">
                  <span className="mr-2" data-id="element-1599">•</span>
                  <span data-id="element-1600">No building segments by hand</span>
                </li>
                <li className="flex items-start" data-id="element-1601">
                  <span className="mr-2" data-id="element-1602">•</span>
                  <span data-id="element-1603">No hunting through CSVs and past campaigns</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8" data-id="element-1604">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1605">
                Send more relevant content
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-1606">
                <li className="flex items-start" data-id="element-1607">
                  <span className="mr-2" data-id="element-1608">•</span>
                  <span data-id="element-1609">Create segment-specific content and offers</span>
                </li>
                <li className="flex items-start" data-id="element-1610">
                  <span className="mr-2" data-id="element-1611">•</span>
                  <span data-id="element-1612">
                    Reduce unsubscribes from "this isn't for me" emails
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8" data-id="element-1613">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1614">
                Grow faster
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-1615">
                <li className="flex items-start" data-id="element-1616">
                  <span className="mr-2" data-id="element-1617">•</span>
                  <span data-id="element-1618">
                    Double down on the segments that open, click, and buy the
                    most
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1619">
                  <span className="mr-2" data-id="element-1620">•</span>
                  <span data-id="element-1621">Spot winning topics and products fast</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8" data-id="element-1622">
              <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1623">
                Pitch sponsors with confidence
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-1624">
                <li className="flex items-start" data-id="element-1625">
                  <span className="mr-2" data-id="element-1626">•</span>
                  <span data-id="element-1627">
                    Show segment-level stats instead of just list size
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1628">
                  <span className="mr-2" data-id="element-1629">•</span>
                  <span data-id="element-1630">
                    Offer targeted placements to the exact audience they care
                    about
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="flex items-center justify-center" data-id="element-1631">
            <div className="w-full max-w-2xl" data-id="element-1632">
              <SegmentDashboardMockup data-id="element-1633" />
            </div>
          </div>
        </div>
      </section>

      {/* Sponsors Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-id="element-1634">
        <div className="max-w-6xl mx-auto" data-id="element-1635">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1636">
            <div data-id="element-1637">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-1638">
                Turn segments into premium ad inventory
              </h2>
              <p className="text-lg text-gray-700 mb-6" data-id="element-1639">
                Use Smart Audience Segmentation to:
              </p>
              <ul className="space-y-4" data-id="element-1640">
                <li className="flex items-start" data-id="element-1641">
                  <span className="mr-3 text-blue-600 font-bold" data-id="element-1642">•</span>
                  <div data-id="element-1643">
                    <span className="font-semibold text-gray-900" data-id="element-1644">
                      Justify higher rates
                    </span>
                    <span className="text-gray-700" data-id="element-1645">
                      {' '}
                      by showing "This segment of 4,000+ solo travelers has X%
                      open rate and Y% CTR."
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1646">
                  <span className="mr-3 text-blue-600 font-bold" data-id="element-1647">•</span>
                  <div data-id="element-1648">
                    <span className="font-semibold text-gray-900" data-id="element-1649">
                      Create sponsor-specific pitches
                    </span>
                    <span className="text-gray-700" data-id="element-1650">
                      {' '}
                      like "Reach our 'Active Buyers' segment—subscribers who
                      frequently click sponsor links."
                    </span>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1651">
                  <span className="mr-3 text-blue-600 font-bold" data-id="element-1652">•</span>
                  <div data-id="element-1653">
                    <span className="font-semibold text-gray-900" data-id="element-1654">
                      Report performance by segment
                    </span>
                    <span className="text-gray-700" data-id="element-1655">
                      {' '}
                      so sponsors see exactly who engaged and why they should
                      rebook.
                    </span>
                  </div>
                </li>
              </ul>
            </div>

            <div className="flex items-center justify-center" data-id="element-1656">
              <div className="w-full max-w-lg" data-id="element-1657">
                <SponsorReportMockup data-id="element-1658" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gray-50" data-id="element-1659">
        <div className="max-w-6xl mx-auto" data-id="element-1660">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center" data-id="element-1661">
            Built for serious Kit.com newsletter creators
          </h2>
          <p className="text-lg text-gray-700 mb-12 text-center max-w-3xl mx-auto" data-id="element-1662">
            Perfect if you:
          </p>
          <ul className="space-y-3 max-w-2xl mx-auto" data-id="element-1663">
            <li className="flex items-start text-lg" data-id="element-1664">
              <span className="mr-3" data-id="element-1665">✓</span>
              <span data-id="element-1666">Send at least 1–2 newsletters per week on Kit.com</span>
            </li>
            <li className="flex items-start text-lg" data-id="element-1667">
              <span className="mr-3" data-id="element-1668">✓</span>
              <span data-id="element-1669">
                Run sponsorships, affiliates, or your own product offers
              </span>
            </li>
            <li className="flex items-start text-lg" data-id="element-1670">
              <span className="mr-3" data-id="element-1671">✓</span>
              <span data-id="element-1672">
                Want smarter targeting without becoming a data analyst
              </span>
            </li>
          </ul>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8" data-id="element-1673">
        <div className="max-w-3xl mx-auto" data-id="element-1674">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center" data-id="element-1675">
            FAQ
          </h2>
          <div className="space-y-4" data-id="element-1676">
            {faqs.map((faq, index) => <div key={index} className="bg-white border border-gray-200 rounded-lg" data-id="element-1677">
              <button onClick={() => toggleFaq(index)} className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors" data-id="element-1678">
                <span className="font-semibold text-gray-900" data-id="element-1679">
                  {faq.question}
                </span>
                <ChevronDownIcon className={`w-5 h-5 text-gray-500 transition-transform ${openFaqIndex === index ? 'transform rotate-180' : ''}`} data-id="element-1680" />
              </button>
              {openFaqIndex === index && <div className="px-6 pb-4" data-id="element-1681">
                <p className="text-gray-700" data-id="element-1682">{faq.answer}</p>
              </div>}
            </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="join-beta" className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gray-50" data-id="element-1683">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1684">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-1685">
            Ready to stop guessing who's on your list?
          </h2>
          <p className="text-xl text-gray-700 mb-10 max-w-2xl mx-auto" data-id="element-1686">
            Turn your Kit.com data into auto-updating audience segments that
            power better content and better sponsorship deals.
          </p>
          <div data-id="element-1687">
            {isSignedUpForBeta ? <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 max-w-md mx-auto" data-id="element-1688">
              <p className="text-xl font-semibold text-gray-900 text-center mb-2" data-id="element-1689">
                🎉 You're signed up for the beta! 🎉
              </p>
              <p className="text-gray-700 text-center" data-id="element-1690">
                You will hear back from us soon.
              </p>
            </div> : <>
              {firstName && <p className="text-lg text-gray-700 mb-4" data-id="element-1691">
                {firstName}, ready to join the beta?
              </p>}
              <button onClick={handleJoinBetaClick} disabled={isSubmitting} className="inline-block px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1692">
                Join the Beta
              </button>
              <p className="text-sm text-gray-600 mt-4" data-id="element-1693">
                We're inviting a small group of Kit.com creators first so we can
                support you closely.
              </p>
            </>}
          </div>
        </div>
      </section>
    </main>}

    {/* Email Dialog */}
    {showEmailDialog && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-1694">
      <div ref={emailDialogRef} className="bg-white rounded-xl shadow-lg max-w-md w-full overflow-hidden" data-id="element-1695">
        <div className="flex justify-end p-4" data-id="element-1696">
          <button onClick={() => setShowEmailDialog(false)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1697">
            <XIcon className="w-6 h-6" data-id="element-1698" />
          </button>
        </div>
        <div className="px-6 pb-6 pt-2" data-id="element-1699">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-2" data-id="element-1700">
            Join the Beta
          </h2>
          <p className="text-gray-600 text-center mb-6" data-id="element-1701">
            Enter your email to join the beta program
          </p>
          <form className="space-y-4" onSubmit={handleEmailSubmit} data-id="element-1702">
            <div data-id="element-1703">
              <input type="email" placeholder="Your email address" required value={emailInput} onChange={e => setEmailInput(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600" data-id="element-1704" />
            </div>
            <button type="submit" className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-lg transition-all hover:shadow-md hover:bg-blue-700 flex items-center justify-center" disabled={isSubmitting} data-id="element-1705">
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </form>
        </div>
      </div>
    </div>}

    <Footer data-id="element-1706" />
  </div>;
}